import { getToken } from '@/util/jwtUtil';
import type { OneSdkConfig, FrankieOneJwt } from '@/integrations/FrankieOne/types';
import { OneSdkLoader } from './oneSdkLoader';
import OneSdkNpm from '@frankieone/one-sdk';
import * as Sentry from '@sentry/vue';

declare const OneSdk; //OneSdk is loaded globally from index.html

export const OnfidoEURestrictedDocumentsConfigOverrides: OneSdkConfig = {
  recipe: {
    ocr: {
      documents: [
        {
          type: 'DRIVERS_LICENCE',
          countries: ['ALL'],
        },
        {
          type: 'PASSPORT',
          countries: ['ALL'],
        },
        {
          type: 'NATIONAL_ID',
          countries: ['ALL'],
        },
      ],
    },
  },
};

export function getOneSdkConfigWithOnfidoOverrides(
  onfidoSdkVersion: string,
  oneSdkConfig: OneSdkConfig
): OneSdkConfig {
  const provider = {
    name: 'onfido',
    sdkVersion: onfidoSdkVersion,
  };
  return {
    ...oneSdkConfig,
    recipe: {
      ...oneSdkConfig.recipe,
      idv: {
        ...oneSdkConfig.recipe?.idv,
        provider,
      },
      ocr: {
        ...oneSdkConfig.recipe?.ocr,
        provider,
      },
      biometrics: {
        ...oneSdkConfig.recipe?.biometrics,
        provider,
      },
    },
  };
}

export function isEuropeanTenant(tenantName?: string): boolean {
  return !!tenantName && / +(EU|eu)$/.test(tenantName);
}

export function oneSdkNpmPackageVersionMatches({
  version,
  packageVersion,
}: {
  version: string;
  packageVersion: string;
}): boolean {
  // cdn version starts with 'v' e.g. v1.8.10, npm version does not have 'v'
  return version.replace(/^v/, '') === packageVersion;
}

export async function initiateOneSdk({
  token,
  version,
  onfidoSdkVersion,
}: {
  token: string;
  version: string;
  onfidoSdkVersion?: string;
}) {
  const localOverrides = JSON.parse(localStorage.getItem('local_onesdk') || '{}');

  const decodedToken = getToken<FrankieOneJwt>(token);

  let oneSdkConfig: OneSdkConfig = {
    session: {
      token,
    },
  };

  const frankieOneTenantName = decodedToken?.data?.organisation?.name;

  if (isEuropeanTenant(frankieOneTenantName)) {
    oneSdkConfig = {
      ...oneSdkConfig,
      ...OnfidoEURestrictedDocumentsConfigOverrides,
    };

    if (onfidoSdkVersion) {
      oneSdkConfig = getOneSdkConfigWithOnfidoOverrides(onfidoSdkVersion, oneSdkConfig);
    }
  }

  const runtimeConfig = Object.assign({}, oneSdkConfig, localOverrides);

  if (oneSdkNpmPackageVersionMatches({ version, packageVersion: OneSdkNpm.version })) {
    Sentry.setTag('oneSdkSource', 'npm');
    return await OneSdkNpm(runtimeConfig);
  } else {
    Sentry.setTag('oneSdkSource', 'cdn');
    await new OneSdkLoader(version).getOneSdk();
    return await OneSdk(runtimeConfig);
  }
}
